/**
 * Page Capabilities Type Definitions
 *
 * Defines page-level capability gating and role-specific access control types.
 * This system extends the organization capabilities to provide granular
 * page-by-page access control with role-specific overrides.
 */

import { CBORoleType, Permission } from './cboRoles';

// ============================================================================
// PAGE CATEGORY TYPES
// ============================================================================

/**
 * Categories for organizing pages in the capability management UI
 */
export type PageCategory =
  | 'core'
  | 'member_management'
  | 'enrollments'
  | 'care_coordination'
  | 'communications'
  | 'analytics'
  | 'billing'
  | 'incentives'
  | 'programs'
  | 'workflows'
  | 'team'
  | 'audits'
  | 'administration'
  | 'integrations'
  | 'employer'
  | 'employee_portal'
  | 'medicaid'
  | 'weavein'
  | 'workforce';

/**
 * Display labels for page categories (English and Spanish)
 */
export const PAGE_CATEGORY_LABELS: Record<PageCategory, { en: string; es: string }> = {
  core: { en: 'Core Pages', es: 'Páginas Principales' },
  member_management: { en: 'Member Management', es: 'Gestión de Miembros' },
  enrollments: { en: 'Enrollments', es: 'Inscripciones' },
  care_coordination: { en: 'Care Coordination', es: 'Coordinación de Cuidado' },
  communications: { en: 'Communications', es: 'Comunicaciones' },
  analytics: { en: 'Analytics & Reporting', es: 'Analíticas y Reportes' },
  billing: { en: 'Billing & Financials', es: 'Facturación y Finanzas' },
  incentives: { en: 'Incentives & Rewards', es: 'Incentivos y Recompensas' },
  programs: { en: 'Programs', es: 'Programas' },
  workflows: { en: 'Workflows', es: 'Flujos de Trabajo' },
  team: { en: 'Team Management', es: 'Gestión de Equipo' },
  audits: { en: 'Audits & Compliance', es: 'Auditorías y Cumplimiento' },
  administration: { en: 'Administration', es: 'Administración' },
  integrations: { en: 'Integrations', es: 'Integraciones' },
  employer: { en: 'Employer Platform', es: 'Plataforma de Empleador' },
  employee_portal: { en: 'Employee Portal', es: 'Portal de Empleados' },
  medicaid: { en: 'Medicaid', es: 'Medicaid' },
  weavein: { en: 'Marketing & Sales', es: 'Marketing y Ventas' },
  workforce: { en: 'Training & Workforce', es: 'Capacitación y Personal' },
};

// ============================================================================
// ACCESS LEVEL TYPES
// ============================================================================

/**
 * Access level for role-specific page permissions
 */
export type AccessLevel = 'enabled' | 'disabled' | 'inherit';

/**
 * Display behavior when a page is disabled
 */
export type DisplayBehavior = 'hide' | 'disable' | 'show-upgrade';

// ============================================================================
// PAGE DEFINITION TYPES
// ============================================================================

/**
 * Definition of a page in the page capabilities registry
 */
export interface PageDefinition {
  id: number;
  page_key: string;
  page_path: string;
  page_name: string;
  page_name_es: string | null;
  description: string | null;
  description_es: string | null;
  parent_capability: string | null;
  category: PageCategory;
  icon: string | null;
  default_enabled: boolean;
  requires_capabilities: string[] | null;
  requires_permissions: Permission[] | null;
  available_for_roles: CBORoleType[] | null;
  exclude_from_roles: CBORoleType[] | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string | null;
}

/**
 * Organization-specific page capability settings
 */
export interface OrgPageCapability {
  id: number;
  organization_id: number;
  page_key: string;
  enabled: boolean;
  config: Record<string, unknown>;
  display_behavior: DisplayBehavior;
  enabled_at: string | null;
  enabled_by: number | null;
  disabled_at: string | null;
  disabled_by: number | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * Page definition with organization-specific status
 */
export interface PageCapabilityWithOrgStatus extends PageDefinition {
  org_enabled: boolean | null;
  org_config: Record<string, unknown>;
  org_display_behavior: DisplayBehavior | null;
  enabled_at: string | null;
  enabled_by: number | null;
  disabled_at: string | null;
  disabled_by: number | null;
  effective_enabled: boolean;
  /** Whether this page's parent capability is enabled for the org (missing = false) */
  parent_capability_enabled: boolean;
}

// ============================================================================
// ROLE PAGE PERMISSION TYPES
// ============================================================================

/**
 * Action-level permissions for a page
 */
export interface PageActionPermissions {
  view?: boolean;
  edit?: boolean;
  delete?: boolean;
  approve?: boolean;
  export?: boolean;
}

/**
 * Role-specific page permission
 */
export interface RolePagePermission {
  id: number | null;
  organization_id: number;
  role_type: CBORoleType;
  page_key: string;
  access_level: AccessLevel;
  permissions: PageActionPermissions;
  notes: string | null;
  created_by: number | null;
  updated_by: number | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Role page permission with page details
 */
export interface RolePagePermissionWithPageInfo extends RolePagePermission {
  page_name: string;
  page_name_es: string | null;
  page_path: string;
  category: PageCategory;
  icon: string | null;
  org_page_enabled: boolean | null;
  default_enabled: boolean;
  exclude_from_roles: CBORoleType[] | null;
  effective_access: boolean;
  is_excluded_by_default: boolean;
}

// ============================================================================
// ROLE CAPABILITY OVERRIDE TYPES
// ============================================================================

/**
 * Role-specific capability override
 */
export interface RoleCapabilityOverride {
  id: number;
  organization_id: number;
  role_type: CBORoleType;
  capability_name: string;
  enabled: boolean;
  config: Record<string, unknown>;
  notes: string | null;
  created_by: number | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string | null;
}

// ============================================================================
// PAGE ACCESS CHECK TYPES
// ============================================================================

/**
 * Result of checking page access
 */
export interface PageAccessCheckResult {
  page_key: string;
  has_access: boolean;
  access_source: 'default' | 'org_page_capability' | 'role_override' | 'role_excluded' | 'parent_capability_disabled' | 'page_not_found';
  enabled: boolean;
  display_behavior: DisplayBehavior;
  permissions: PageActionPermissions;
}

/**
 * Bulk page access check result
 */
export type BulkPageAccessResult = Record<string, {
  has_access: boolean;
  access_source: string;
  enabled: boolean;
  display_behavior: DisplayBehavior;
}>;

// ============================================================================
// API REQUEST/RESPONSE TYPES
// ============================================================================

/**
 * Request to update an organization's page capability
 */
export interface OrgPageCapabilityUpdateRequest {
  enabled: boolean;
  config?: Record<string, unknown>;
  display_behavior?: DisplayBehavior;
}

/**
 * Request to bulk update page capabilities
 */
export interface BulkPageCapabilityUpdateRequest {
  updates: Array<{
    page_key: string;
    enabled: boolean;
    config?: Record<string, unknown>;
    display_behavior?: DisplayBehavior;
  }>;
}

/**
 * Request to update a role's page permission
 */
export interface RolePagePermissionUpdateRequest {
  page_key: string;
  access_level: AccessLevel;
  permissions?: PageActionPermissions;
  notes?: string;
}

/**
 * Request to bulk update role page permissions
 */
export interface BulkRolePagePermissionUpdateRequest {
  role_type: CBORoleType;
  permissions: RolePagePermissionUpdateRequest[];
}

// ============================================================================
// ROLE DEFINITIONS
// ============================================================================

/**
 * CBO Role with restriction info
 */
export interface CBORoleInfo {
  type: CBORoleType;
  display_name: string;
  display_name_es: string;
  description: string;
  can_be_restricted: boolean;
}

/**
 * Available CBO roles for page permission configuration
 * Note: promotora and care_coordinator are now organization-specific terminologies
 * that map to the 'staff' system role.
 */
export const CBO_ROLES: CBORoleInfo[] = [
  {
    type: 'super_admin',
    display_name: 'Super Administrator',
    display_name_es: 'Super Administrador',
    description: 'Full platform access with all permissions',
    can_be_restricted: false,
  },
  {
    type: 'org_admin',
    display_name: 'Organization Administrator',
    display_name_es: 'Administrador de Organización',
    description: 'Organization-wide administration and configuration',
    can_be_restricted: false,
  },
  {
    type: 'program_manager',
    display_name: 'Program Manager',
    display_name_es: 'Gerente de Programa',
    description: 'Manages programs, reviews reports, and oversees compliance',
    can_be_restricted: true,
  },
  {
    type: 'supervisor',
    display_name: 'Supervisor',
    display_name_es: 'Supervisor',
    description: 'Team supervision, case review, and compliance auditing',
    can_be_restricted: true,
  },
  {
    type: 'staff',
    display_name: 'Staff',
    display_name_es: 'Personal',
    description: 'Standard member and enrollment access (includes field workers)',
    can_be_restricted: true,
  },
  {
    type: 'viewer',
    display_name: 'Viewer',
    display_name_es: 'Observador',
    description: 'Read-only access to assigned areas',
    can_be_restricted: true,
  },
];

/**
 * Get restrictable roles (excludes super_admin and org_admin)
 */
export function getRestrictableRoles(): CBORoleInfo[] {
  return CBO_ROLES.filter(role => role.can_be_restricted);
}

// ============================================================================
// MATRIX VIEW TYPES
// ============================================================================

/**
 * Role permission matrix entry
 */
export interface RolePermissionMatrixEntry {
  access_level: AccessLevel;
  is_excluded_by_default: boolean;
  effective_access: boolean;
}

/**
 * Page row in the permission matrix
 */
export interface PermissionMatrixRow {
  page_key: string;
  page_name: string;
  page_name_es: string | null;
  category: PageCategory;
  org_enabled: boolean;
  roles: Record<CBORoleType, RolePermissionMatrixEntry>;
}

/**
 * Full permission matrix response
 */
export interface PermissionMatrix {
  roles: CBORoleType[];
  matrix: PermissionMatrixRow[];
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Page capability state for React components
 */
export interface PageCapabilityState {
  enabled: boolean;
  hasAccess: boolean;
  loading: boolean;
  config: Record<string, unknown>;
  displayBehavior: DisplayBehavior;
  permissions: PageActionPermissions;
  accessSource: string;
}

/**
 * Pages grouped by category
 */
export type PagesByCategory = Record<PageCategory, PageCapabilityWithOrgStatus[]>;

/**
 * Group pages by category
 */
export function groupPagesByCategory(pages: PageCapabilityWithOrgStatus[]): PagesByCategory {
  return pages.reduce((acc, page) => {
    const category = page.category as PageCategory;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(page);
    return acc;
  }, {} as PagesByCategory);
}

/**
 * Get category label for display
 */
export function getCategoryLabel(category: PageCategory, locale: 'en' | 'es' = 'en'): string {
  return PAGE_CATEGORY_LABELS[category]?.[locale] || category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}
